import axios from 'axios';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _difference from 'lodash/difference';

import router from '@/router';
import { defaultKeys, insertKeys } from '@/lib/vuexUtils';
import { mergeArraysByKey, formatUqlDateText, post, ruid, isDayOp, isPresent } from '@/lib/utils';
import { constants, translateWilds } from '@/lib/constants';

const csvExcluded = {
  CreditApplication: [
    // Refactor server to work out dotted paths? (#15302)
    'customeraccount.creditlimit',
  ],
  InstallmentContract: [
    // Refactor server to work out dotted paths? (#15302)
    'customer.firstname',
    'customer.lastname',
  ],
  Product: ['sale'],
};

// For axios to cancel requests
const CancelToken = axios.CancelToken;
let _cancel;
let _cancelableRequests = [];
const _cancelTokenCallback = cancelId => {
  /* istanbul ignore next */
  return new CancelToken(((c) => {
    // For cancelling on the front-end
    // cancel is called from search/cancelSearch action
    _cancel = c;
    // For cancelling on the backend
    _cancelableRequests.push(cancelId);
  }));
};

const updateSearchCache = async (dispatch, payload) => {
  const path = router.currentRoute._rawValue.path;
  dispatch('cached/updateCachedSearch', { keys: payload, path, className: payload.className, isPaginated: payload.isPaginated, queryOverrides: payload.queryOverrides }, { root: true });
};

const localState = {
  // camelCase
  schema: null,
  listActionItems: {},
  limit: 100,
  newQuery: false,
  updatedRecords: [],
  selectedRecords: [],
  paginatedRecords: [],
  csvQueryInFlight: false,
};

const localGetters = {
  // underscore_lowercase
  schema: state => state.schema,
  actionListItems: state => state.listActionItems,
  limit: state => state.limit,
  newQuery: state => state.newQuery,
  updatedRecords: state => state.updatedRecords,
  selectedRecords: state => state.selectedRecords,
  paginatedRecords: state => state.paginatedRecords,
  csvQueryInFlight: state => state.csvQueryInFlight,
};

const localActions = {
  setUpdatedRecords({ commit }, records) {
    if (records) commit('set_updated_records', records);
    else commit('set_updated_records', []);
  },

  async setNewQuery({ commit }, bool) {
    await commit('set_new_query', bool);
  },

  async updateResults({ dispatch, state }, currentRecords) {
    await dispatch('callInsertKeys', state.updatedRecords);
    return mergeArraysByKey(currentRecords, state.updatedRecords, '_id');
  },

  async updateCache({ dispatch }, payload) {
    await updateSearchCache(dispatch, payload);
  },

  async fetchActionListItems({ state, commit, rootGetters, dispatch }, className) {
    if (state.listActionItems[className]) return;
    const { results, errors } = await dispatch('editObject/query', { url: '/tg/action/fetch_actions', params: { classname: className } }, { root: true });
    if (errors) return;
    const filteredResults = results.reduce((acc, val) => {
      if (val.permissionname) {
        const hasPermission = rootGetters['permissions/isPermPresent'](val.permissionname);
        if (hasPermission === false) return acc;
      }
      if (!val.displaylabel) val.actionlabel = rootGetters['translations/label'](`{${className}.action_${val.action}}l`);
      if (!val.actionlabel) val.actionlabel = rootGetters['translations/label']('{Continue?}l');
      acc.push(val);
      return acc;
    }, []);
    commit('set_list_action_items', { results: filteredResults, className });
    return filteredResults;
  },

  /* istanbul ignore next */
  cancelSearch({ dispatch }, title = 'Search Cancelled') {
    /* istanbul ignore next */
    if (_cancel) {
      _cancel('Request Cancelled');
      for (const requestid of _cancelableRequests) {
        post('/tg/system/cancel_request', { requestid });
      }
      dispatch('notify/push', {
        title,
        type: 'info',
      }, { root: true });
      _cancelableRequests = [];
    }
  },

  async smartQuery(ctx, { className, data }) {
    const { dispatch } = ctx;
    const cancelId = ruid();
    const { results } = await dispatch('editObject/query', {
      url: `/tg/${className.toLowerCase()}/smart_query`,
      params: { [className]: data, cancelable_requestid: cancelId },
      additionalConfig: { cancelToken: _cancelTokenCallback(cancelId) },
      insertKeys: true,
    }, { root: true });
    _cancelableRequests = [];
    updateSearchCache(dispatch, { className, results, filterData: data });
    return results;
  },

  // eslint-disable-next-line max-len
  async executeSearch(ctx, { className, offset = 0, previousResults, additionalParams = {}, filterData = null, filterid = null, limit, str = '', createSearchString = true, cacheResults = true, paginationParams = {}, queryOverrides = [] }) {
    const { dispatch, commit, state, rootGetters } = ctx;
    try {
      if (!rootGetters['editObject/searchInFlight']) dispatch('editObject/setSearchInFlight', true, { root: true });
      const searchString = !createSearchString ? '' : rootGetters['cached/searchString']({ className, path: router.currentRoute._rawValue.path });
      const url = `/tg/${className.toLowerCase()}/${str || 'query'}`;
      const params = {};
      if (createSearchString) {
        params.offset = offset;
        params.limit = limit || state.limit;
        params.q = searchString;
      }
      const pagination = { params: { url, params: { q: searchString } }, pagination: {} };
      const cancelId = ruid();
      params.cancelable_requestid = cancelId;
      if (additionalParams) {
        for (const key of Object.keys(additionalParams)) {
          params[key] = additionalParams[key];
          pagination.params.params[key] = additionalParams[key];
        }
      }
      const search = await post(url, params, true, {
        cancelToken: _cancelTokenCallback(cancelId),
      });
      _cancelableRequests = [];
      let results = _get(search, 'results', []);
      await dispatch('callInsertKeys', results);
      pagination.pagination = {
        limit,
        offset,
        recordCount: _get(search.metaData, 'data.response.recordcount', 0),
        params,
        url,
        page: offset === 0 ? 1 : paginationParams.page,
      };
      const cachePayload = {
        className,
        results,
        query: searchString,
        isPaginated: true,
        pagination,
        filterid,
        queryOverrides,
      };
      if (filterData) cachePayload.filterData = filterData;
      if (cacheResults) updateSearchCache(dispatch, cachePayload);
      if (previousResults && previousResults.length) {
        results = mergeArraysByKey(previousResults, results, '_id');
      }
      await commit('set_new_query', false);
      return { response: search.response, search: search.metaData, results, pagination };
    } finally {
      if (rootGetters['editObject/searchInFlight']) dispatch('editObject/setSearchInFlight', false, { root: true });
    }
  },

  // TODO: convert to util.query
  async executeDataGenerator(ctx, payload) {
    const { dispatch, rootGetters } = ctx;
    const { datageneratorid, args, cacheResults = true } = payload;
    try {
      if (!rootGetters['editObject/searchInFlight']) dispatch('editObject/setSearchInFlight', true, { root: true });
      const cancelId = ruid();
      const params = {};
      params.datageneratorid = datageneratorid;
      params.arguments = args;
      params.cancelable_requestid = cancelId;
      const search = await post('/tg/datagenerator/run', params, false, {
        cancelToken: _cancelTokenCallback(cancelId),
      });
      _cancelableRequests = [];
      const results = search.results;
      await dispatch('callInsertKeys', results);
      if (cacheResults) updateSearchCache(dispatch, { ...payload, results, isPaginated: true });
      return { search, results };
    } finally {
      if (rootGetters['editObject/searchInFlight']) dispatch('editObject/setSearchInFlight', false, { root: true });
    }
  },

  async downloadCSV(ctx, { className, headers, csvParamsFn, dataGenerator, path }) {
    const { commit, dispatch, rootGetters } = ctx;
    const headerValues = headers.map(({ value }) => value);
    const csv_fields = _difference(headerValues, csvExcluded[className]);
    try {
      commit('set_csv_query_in_flight', true);
      const csvRequestId = ruid();
      let payload = { csv_fields, cancelable_requestid: csvRequestId };
      let url;
      if (csvParamsFn) {
        const params = csvParamsFn();
        if (!params) {
          dispatch('notify/push', {
            title: 'Error',
            text: 'Unable to download CSV.',
            type: 'error',
          }, { root: true });
          return;
        }
        url = params.url;
        payload = { ...payload, ...params.params };
      }
      else if (dataGenerator) {
        url = '/tg/datagenerator/csv_run';
        const { datageneratorid, args } = dataGenerator;
        payload.datageneratorid = datageneratorid;
        payload.arguments = args;
        payload.classname = className;
      }
      else {
        url = `/tg/${className.toLowerCase()}/csv_query`;
        const q = rootGetters['cached/searchString']({ className, path });
        payload.q = q;
      }
      await post(url, payload, false, {
        cancelToken: _cancelTokenCallback(csvRequestId),
      });
    }
    finally {
      commit('set_csv_query_in_flight', false);
      _cancelableRequests = [];
    }
  },

  callInsertKeys({ rootState }, results) {
    return results.forEach((record, index) => {
      results[index] = insertKeys(defaultKeys, record, rootState.meta);
    });
  },

  editRecord({ rootGetters }, payload) {
    const record = payload.record;
    const additionalParams = payload.additionalParams || {};
    const pk = {};
    let queryParamKeys = {};
    const keys = rootGetters['meta/primary_keys'](record._classname);
    pk.id = record[keys.pop()];
    if (keys.length > 0) {
      queryParamKeys = keys.reduce((acc, key) => {
        acc[key] = record[key];
        return acc;
      }, {});
    }
    const routeParams = {
      // If we need to return to DynaformEdit
      path: `/${payload.overrideClassname || record._classname}/${encodeURIComponent(pk.id)}`,
      query: { ...queryParamKeys, ...additionalParams },
    };
    if (payload.replace) {
      router.replace(routeParams);
    }
    else if (payload.inNewWindow) {
      window.open(router.resolve(routeParams).href, '_blank');
    }
    else {
      router.push(routeParams);
    }
  },

  async newRecord(ctx, payload) {
    await router.push({
      path: `/${payload.routeParams.classname}/new`,
    });
  },

  setSelectedRecords({ commit }, records) {
    commit('set_selected_records', records);
  },

  setPaginatedRecords({ commit }, records) {
    commit('set_paginated_records', records);
  },

  generateUql({ rootGetters }, { collection, meta, queryOverrides = [], createUqlWithPassedMeta = false, comboUQLOverrides = [] }) {
    const { CONTAINSIGNORECASE, EQUALS, ISNOTNULL, DELIM } = constants;
    let q = '';
    Object.keys(collection).forEach((fieldname) => {
      // There are two sources of truth for meta here, but changing to use what's passed may break assumptions in siutations where BaseSearch's executeSearch callback is used.
      const fieldMeta = createUqlWithPassedMeta ? _get(meta, `properties[${fieldname}]`) : rootGetters['meta/property'](meta.classname, fieldname);

      let operator = `${CONTAINSIGNORECASE}`;
      if (fieldMeta) {
        let value = collection[fieldname];
        if (fieldMeta.property_meta.flag && value !== null) {
          value = value ? 'Y' : null;
          operator = `${EQUALS}`;
        } else if (value === '*') {
          operator = `${ISNOTNULL}`;
        } else if (typeof value === 'string' && value.indexOf('*') >= 0) {
          value = translateWilds(value);
        } else if (fieldMeta.type === 'number') {
          operator = `${EQUALS}`;
        } else if ((fieldMeta.format === 'date-time' || fieldMeta.format === 'date') && !isDayOp(queryOverrides, fieldname) && isPresent(value)) {
          value = formatUqlDateText(value);
          operator = `${EQUALS}`;
        } else if (fieldMeta.property_meta.dataprovider || fieldMeta.enum) {
          operator = `${EQUALS}`;
        }
        const overrideHandler = (overrideArr) => {
          const [operatorOverride] = overrideArr.filter((override) => override.name === fieldname);
          if (operatorOverride) {
            const qType = operatorOverride.queryType;
            operator = qType;
            const valuelessTypes = ['NL', 'NN'];
            if (valuelessTypes.includes(qType)) {
              value = '';
              q += `${fieldname}-${operator}${value}${DELIM}`;
            }
          }
        };
        if (queryOverrides.length) overrideHandler(queryOverrides);
        if (comboUQLOverrides.length) overrideHandler(comboUQLOverrides);
        if (value) {
          if (value === '*') value = ''; // Handle wildcard searches

          // Handle multiselect dataproviders as those are collections
          if (fieldMeta.property_meta.dataprovider && Array.isArray(value)) {
            q += 'or(';
            for (let i = 0; i < value.length; i += 1) {
              q += `${fieldname}-${operator}${value[i]}${DELIM}`;
            }
            q += `)${DELIM}`;
          } else {
            q += `${fieldname}-${operator}${value}${DELIM}`;
          }
        }
      }
    });
    return q;
  },

  async getLockedInventory(ctx, params) {
    const { results } = await post('/tg/lockedinventory/query', params);
    return results;
  },
};

const localMutations = {
  // underscore_case
  set_schema(state, schema) {
    state.schema = schema;
  },

  set_list_action_items(state, { results, className }) {
    _set(state, `listActionItems.${className}`, results);
  },

  set_updated_records(state, records) {
    state.updatedRecords = records;
  },

  set_new_query(state, bool) {
    state.newQuery = bool;
  },

  set_selected_records(state, records) {
    state.selectedRecords = records;
  },

  set_paginated_records(state, records) {
    state.paginatedRecords = records;
  },

  set_csv_query_in_flight(state, bool) {
    state.csvQueryInFlight = bool;
  },
};

export default {
  namespaced: true,
  state: localState,
  getters: localGetters,
  actions: localActions,
  mutations: localMutations,
};
